import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import useFocusTrap from '../hooks/useFocusTrap';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import Button from '../primitives/Button';
import Wordmark from '../primitives/Wordmark';
import { NAV_CTA, NAV_LOGIN, type NavItem, type NavKey } from '../content/nav';

interface MobileMenuProps {
  id: string;
  items: NavItem[];
  activePage?: NavKey | string;
  onClose: () => void;
}

/**
 * Full-screen Bone sheet below 768px. Focus is trapped inside, Escape closes,
 * the page behind stops scrolling, and focus returns to the Menu button when
 * it unmounts (useFocusTrap restores it).
 */
export default function MobileMenu({ id, items, activePage, onClose }: MobileMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  useFocusTrap(ref, true, { initialFocus: closeRef, onEscape: onClose });
  useBodyScrollLock(true);

  return (
    <div ref={ref} id={id} className="mk-menu" role="dialog" aria-modal="true" aria-label="Menu">
      <div className="mk-menu__bar">
        <Link to="/" className="mk-nav__brand" onClick={onClose}>
          <Wordmark title="Melio" />
        </Link>
        <button ref={closeRef} type="button" className="mk-nav__menu-btn t-details" onClick={onClose}>
          Close
        </button>
      </div>
      <nav aria-label="Primary">
        <ul className="mk-menu__list t-pillar">
          {items.map((item) => (
            <li key={item.key}>
              <Link to={item.to} aria-current={activePage === item.key ? 'page' : undefined} onClick={onClose}>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="mk-menu__foot">
        <Link to={NAV_LOGIN.to} className="t-details" onClick={onClose}>
          {NAV_LOGIN.label}
        </Link>
        <Button variant="fill" to={NAV_CTA.to}>
          {NAV_CTA.label}
        </Button>
      </div>
    </div>
  );
}
